import React, { useEffect, useRef, useReducer } from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'
import BigSpinner from './BigSpinner'

interface WorkflowState {
    currentPhase: number
    loadingResult: unknown
    userInput: unknown
    userInputSetup: { component: React.ReactNode; title: string } | undefined
    confirmationText: React.ReactNode | undefined
    validationErrors: { message?: string; [key: string]: unknown } | undefined
    validationData: unknown
    postValidationConfirmation: React.ReactNode | undefined
    executionResult: unknown
    error: { message?: string } | string | undefined
}

interface ValidationOutcome {
    success: boolean
    data?: unknown
    errors?: { message?: string; [key: string]: unknown }
}

interface Props {
    parameters?: unknown
    loadingStep?: (parameters: unknown) => Promise<unknown>
    confirmationStep?: (parameters: unknown, loadingResult: unknown) => React.ReactNode
    userInputStep?: (
        parameters: unknown,
        loadingResult: unknown,
        previousValues: unknown,
        validationErrors: { message?: string; [key: string]: unknown } | undefined,
        completionCallback: (input: unknown) => void
    ) => { component: React.ReactNode; title: string }
    validationStep?: (parameters: unknown, loadingResult: unknown, userInput: unknown) => Promise<ValidationOutcome>
    /**
     * Rendered after the validation step succeeds and before execution. Receives whatever the
     * validation step returned in `data`. The user must Confirm before the workflow proceeds.
     */
    postValidationConfirmationStep?: (
        parameters: unknown,
        loadingResult: unknown,
        userInput: unknown,
        validationData: unknown
    ) => React.ReactNode
    /**
     * When true, the post-validation confirmation phase renders a "Back" button instead of
     * "Cancel", which jumps back to the user input step preserving the previously entered
     * values (passed as `previousValues` to `userInputStep`).
     */
    canGoBackFromPostValidation?: boolean
    backFromPostValidationLabel?: string
    executionStep: (parameters: unknown, loadingResult: unknown, userInput: unknown) => Promise<unknown>
    completionCallback: (result: unknown) => void
    static?: boolean
}

const ProcessState = Object.freeze({
    "LOAD_INITIAL": 1,
    "WAITING_CONFIRMATION": 2,
    "WAITING_USER": 3,
    "VALIDATION": 4,
    "POST_VALIDATION_CONFIRMATION": 5,
    "EXECUTING": 6,
    "DONE": 7
})

function OperationWorkflowModal(props: Props) {
    const [, forceUpdate] = useReducer((n: number) => n + 1, 0)

    const propsRef = useRef(props)
    propsRef.current = props

    const s = useRef<WorkflowState>({
        currentPhase: 0,
        loadingResult: undefined,
        userInput: undefined,
        userInputSetup: undefined,
        confirmationText: undefined,
        validationErrors: undefined,
        validationData: undefined,
        postValidationConfirmation: undefined,
        executionResult: undefined,
        error: undefined,
    }).current

    const startedRef = useRef(false)

    function cancelWorkflow() {
        s.currentPhase = ProcessState.DONE
        forceUpdate()
        propsRef.current.completionCallback(false)
    }

    function advancePhase(jumpToPhase?: number) {
        const p = propsRef.current
        if (s.currentPhase === ProcessState.DONE || s.error) return

        let newPhase = jumpToPhase !== undefined ? jumpToPhase : s.currentPhase + 1

        if (newPhase === ProcessState.LOAD_INITIAL) {
            if (!p.loadingStep) {
                newPhase = ProcessState.WAITING_CONFIRMATION
            } else {
                s.currentPhase = ProcessState.LOAD_INITIAL
                forceUpdate()
                p.loadingStep(p.parameters)
                    .then(result => { s.loadingResult = result; advancePhase() })
                    .catch(err => { s.error = err; forceUpdate() })
                return
            }
        }

        if (newPhase === ProcessState.WAITING_CONFIRMATION) {
            if (!p.confirmationStep) {
                newPhase = ProcessState.WAITING_USER
            } else {
                s.confirmationText = p.confirmationStep(p.parameters, s.loadingResult)
                s.currentPhase = ProcessState.WAITING_CONFIRMATION
                forceUpdate()
                return
            }
        }

        if (newPhase === ProcessState.WAITING_USER) {
            if (!p.userInputStep) {
                newPhase = ProcessState.EXECUTING
            } else {
                s.userInputSetup = p.userInputStep(
                    p.parameters, s.loadingResult, s.userInput, s.validationErrors,
                    (input) => { s.userInput = input; advancePhase() }
                )
                s.currentPhase = ProcessState.WAITING_USER
                forceUpdate()
                return
            }
        }

        if (newPhase === ProcessState.VALIDATION) {
            if (!p.validationStep) {
                newPhase = ProcessState.POST_VALIDATION_CONFIRMATION
            } else {
                s.currentPhase = ProcessState.VALIDATION
                forceUpdate()
                p.validationStep(p.parameters, s.loadingResult, s.userInput)
                    .then(r => {
                        if (r && r.success) {
                            s.validationErrors = undefined
                            s.validationData = r.data
                            advancePhase()
                        } else {
                            s.validationErrors = r?.errors
                            s.validationData = undefined
                            advancePhase(ProcessState.WAITING_USER)
                        }
                    })
                return
            }
        }

        if (newPhase === ProcessState.POST_VALIDATION_CONFIRMATION) {
            if (!p.postValidationConfirmationStep) {
                newPhase = ProcessState.EXECUTING
            } else {
                s.postValidationConfirmation = p.postValidationConfirmationStep(
                    p.parameters, s.loadingResult, s.userInput, s.validationData
                )
                s.currentPhase = ProcessState.POST_VALIDATION_CONFIRMATION
                forceUpdate()
                return
            }
        }

        if (newPhase === ProcessState.EXECUTING) {
            s.currentPhase = ProcessState.EXECUTING
            forceUpdate()
            p.executionStep(p.parameters, s.loadingResult, s.userInput)
                .then(result => { s.executionResult = result; advancePhase() })
                .catch(err => { s.error = (err as any).message || err; forceUpdate() })
            return
        }

        s.currentPhase = newPhase
        forceUpdate()
        if (newPhase === ProcessState.DONE) {
            propsRef.current.completionCallback(s.executionResult)
        }
    }

    useEffect(() => {
        // Guard against React 18 Strict Mode double-invocation of effects in
        // dev. Without this, advancePhase() runs twice synchronously: the first
        // call kicks off loadingStep (still pending), the second call increments
        // past LOAD_INITIAL and reaches WAITING_USER with loadingResult still
        // undefined, crashing any userInputStep that derefs the loading result.
        if (startedRef.current) return
        startedRef.current = true
        advancePhase()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    if (s.currentPhase === ProcessState.DONE) return null

    const showCloseButton = props.static ? undefined : true

    let content: React.ReactNode = null
    if (s.error) {
        content = <>
            <Modal.Header closeButton={showCloseButton}>
                <Modal.Title>Error</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="danger">{(s.error as any).message || s.error as string}</Alert>
            </Modal.Body>
        </>
    } else {
        switch (s.currentPhase) {
            case ProcessState.LOAD_INITIAL:
                content = <BigSpinner />
                break
            case ProcessState.WAITING_CONFIRMATION:
                content = <>
                    <Modal.Header closeButton={showCloseButton}>
                        <Modal.Title>Please confirm</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{s.confirmationText}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={cancelWorkflow}>Cancel</Button>
                        <Button variant="primary" onClick={() => advancePhase()}>Confirm</Button>
                    </Modal.Footer>
                </>
                break
            case ProcessState.WAITING_USER:
                content = <>
                    <Modal.Header closeButton={showCloseButton}>
                        <h2>{s.userInputSetup?.title}</h2>
                    </Modal.Header>
                    {s.validationErrors
                        ? <Alert variant="danger">Validation errors: {s.validationErrors.message}</Alert>
                        : ''}
                    {s.userInputSetup?.component}
                </>
                break
            case ProcessState.VALIDATION:
                content = <BigSpinner />
                break
            case ProcessState.POST_VALIDATION_CONFIRMATION:
                content = <>
                    <Modal.Header closeButton={showCloseButton}>
                        <Modal.Title>Please confirm</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{s.postValidationConfirmation}</Modal.Body>
                    <Modal.Footer>
                        {props.canGoBackFromPostValidation
                            ? <Button variant="secondary" onClick={() => advancePhase(ProcessState.WAITING_USER)}>{props.backFromPostValidationLabel ?? 'Back'}</Button>
                            : <Button variant="secondary" onClick={cancelWorkflow}>Cancel</Button>}
                        <Button variant="primary" onClick={() => advancePhase()}>Confirm</Button>
                    </Modal.Footer>
                </>
                break
            case ProcessState.EXECUTING:
                content = <BigSpinner />
                break
            default:
                break
        }
    }

    if (props.static) {
        return <Modal.Dialog>{content}</Modal.Dialog>
    } else {
        return <Modal show={true} onHide={() => propsRef.current.completionCallback(false)}>
            {content}
        </Modal>
    }
}

export default OperationWorkflowModal